import { Fragment } from "react";
import {
  DetailPageSummaryBar,
  SummaryBarCell,
  SummaryBarCellBorder,
} from "../../SummaryBar/Detail";
import {
  Bone,
  DetailTopBar,
  DetailTopBarSide,
  ItemsHeader,
  Page,
  Table,
  TableHead,
  TableRow,
  TabsRow,
} from "./DetailPageSkeleton.styles";

/**
 * Shared staff detail-page skeleton (top bar, summary strip, tabs, table).
 *
 * Pass as `placeholder` on `DetailPageProvider`, or rely on the provider
 * default (`placeholder={true}`) so every detail module gets this layout
 * while the header GET is in flight.
 *
 * @param {Object} props
 * @param {string} [props.label="Loading detail"] Accessible label.
 * @param {number} [props.summaryCells=6] Number of summary-bar cells.
 * @param {number} [props.tabCount=3] Number of tab placeholders.
 * @param {number} [props.actionButtons=2] Number of top-bar action bones.
 * @param {number} [props.tableColumns=10] Number of table columns.
 * @param {number} [props.tableRows=4] Number of table body rows.
 * @return {JSX.Element}
 */
export const DetailPageSkeleton = ({
  label = "Loading detail",
  summaryCells = 8,
  tabCount = 3,
  actionButtons = 2,
  tableColumns = 10,
  tableRows = 4,
}) => (
  <Page aria-busy="true" aria-label={label}>
    <DetailTopBar>
      <DetailTopBarSide>
        <Bone $w="24px" $h="24px" />
        <Bone $w="180px" $h="24px" />
      </DetailTopBarSide>
      <DetailTopBarSide>
        {Array.from({ length: actionButtons }).map((_, i) => (
          <Bone key={i} $w={i === 0 ? "120px" : "88px"} $h="36px" />
        ))}
      </DetailTopBarSide>
    </DetailTopBar>

    <DetailPageSummaryBar>
      {Array.from({ length: summaryCells }).map((_, i) => (
        <Fragment key={i}>
          <SummaryBarCell>
            <Bone $w="72px" $h="12px" />
            <Bone $w="110px" $h="16px" />
          </SummaryBarCell>
          {i < summaryCells - 1 && <SummaryBarCellBorder />}
        </Fragment>
      ))}
    </DetailPageSummaryBar>

    {tabCount > 0 && (
      <TabsRow>
        {Array.from({ length: tabCount }).map((_, i) => (
          <Bone key={i} $w={i === 0 ? "90px" : i === 1 ? "130px" : "72px"} $h="22px" />
        ))}
      </TabsRow>
    )}

    <ItemsHeader>
      <Bone $w="160px" $h="32px" />
      <Bone $w="88px" $h="32px" />
    </ItemsHeader>

    <Table>
      <TableHead $columns={tableColumns}>
        {Array.from({ length: tableColumns }).map((_, i) => (
          <Bone key={i} $h="12px" />
        ))}
      </TableHead>
      {Array.from({ length: tableRows }).map((_, row) => (
        <TableRow key={row} $columns={tableColumns}>
          {Array.from({ length: tableColumns }).map((_, col) => (
            <Bone key={col} $h="20px" />
          ))}
        </TableRow>
      ))}
    </Table>
  </Page>
);
